import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserManager, User, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import logger from '../utils/logger';
import config from '../config';

// OIDC Configuration based on CLAUDE.md specifications
const oidcConfig: UserManagerSettings = {
  authority: config.keycloakUrl,
  client_id: config.clientId,
  redirect_uri: config.redirectUri,
  post_logout_redirect_uri: config.logoutUri,
  response_type: 'code',
  scope: 'openid profile email',

  // Token settings
  automaticSilentRenew: true,
  silent_redirect_uri: window.location.origin + '/auth/silent-callback',
  accessTokenExpiringNotificationTimeInSeconds: config.tokenRefreshThreshold,

  // Security settings
  filterProtocolClaims: true,
  loadUserInfo: false, // Disabled - ID token contains sufficient user info

  // Storage settings - configurable via environment
  userStore: new WebStorageStateStore({
    store: config.useSessionStorage ? window.sessionStorage : window.localStorage
  }),
  
  // Additional settings
  includeIdTokenInSilentRenew: true, // Include ID token for better compatibility
  clockSkewInSeconds: 60, // Allow 60 seconds clock skew
  
  // Development workaround for HTTP
  // This allows HTTP in development but should NEVER be used in production
  revokeTokensOnSignout: true,
  monitorSession: false, // Disable session monitoring to avoid HTTPS requirement
  silentRequestTimeoutInSeconds: 30, // Longer timeout for silent refresh
};

interface AuthContextType {
  user: User | null;
  userManager: UserManager;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userManager] = useState(() => new UserManager(oidcConfig));

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Try to get existing user from storage
        const existingUser = await userManager.getUser();
        
        if (existingUser && !existingUser.expired) {
          setUser(existingUser);
          logger.authEvent('user_loaded_from_storage', {
            userId: existingUser.profile.sub,
            email: existingUser.profile.email,
          });
        } else if (existingUser && existingUser.expired) {
          logger.authEvent('token_expired', {
            userId: existingUser.profile.sub,
            expiry: existingUser.expires_at,
          });
          // Try to silently renew
          try {
            const renewedUser = await userManager.signinSilent();
            setUser(renewedUser);
            if (renewedUser) {
              logger.authEvent('token_renewed_silently', {
                userId: renewedUser.profile.sub,
              });
            }
          } catch (renewError) {
            logger.error('Silent token renewal failed', {
              error: renewError instanceof Error ? renewError.message : 'Unknown error',
            });
            setUser(null);
          }
        }
      } catch (initError) {
        logger.error('Auth initialization failed', {
          error: initError instanceof Error ? initError.message : 'Unknown error',
        });
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [userManager]);

  // Set up event handlers
  useEffect(() => {
    const handleUserLoaded = (loadedUser: User) => {
      logger.authEvent('user_loaded', {
        userId: loadedUser.profile.sub,
        email: loadedUser.profile.email,
      });
      setUser(loadedUser);
      setError(null);
    };

    const handleUserUnloaded = () => {
      logger.authEvent('user_unloaded');
      setUser(null);
    };

    const handleAccessTokenExpiring = () => {
      logger.authEvent('token_expiring');
    };

    const handleAccessTokenExpired = () => {
      logger.authEvent('token_expired');
      setUser(null);
    };

    const handleSilentRenewError = (error: Error) => {
      logger.error('Silent renewal error', {
        error: error.message,
      });
      setError('Session renewal failed. Please login again.');
    };

    const handleUserSignedOut = () => {
      logger.authEvent('user_signed_out');
      setUser(null);
    };

    // Add event listeners
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    // Cleanup
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, [userManager]);

  const login = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      logger.authEvent('login_initiated');
      await userManager.signinRedirect();
    } catch (loginError) {
      const errorMessage = loginError instanceof Error ? loginError.message : 'Login failed';
      logger.error('Login error', { error: errorMessage });
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [userManager]);

  const logout = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      logger.authEvent('logout_initiated', {
        userId: user?.profile?.sub,
      });
      
      await userManager.signoutRedirect();
    } catch (logoutError) {
      const errorMessage = logoutError instanceof Error ? logoutError.message : 'Logout failed';
      logger.error('Logout error', { error: errorMessage });
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [userManager, user]);

  const contextValue: AuthContextType = {
    user,
    userManager,
    isAuthenticated: !!user && !user.expired,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;